import {
  ADD_BUILDINGS,
  START_BUILDINGS_LOAD,
  INVALIDATE_BUILDINGS,
  DELETE_ALL_BUILDINGS,
  ADD_MULTIPLE_LOCATIONS,
  DELETE_MULTIPLE_LOCATIONS,
} from './actiontypes';
import { addMultipleEquipmentAndPointsAction } from './equipment';
import {
  getAll,
  patchBuilding,
  deleteBuilding,
  createBuilding,
  getAllLocationsForBuilding,
  createRelationships,
  createContainsRelationships,
  deleteRelationships,
  deleteContainsRelationships,
  createLocationApi,
  modifyLocationsApi,
  deleteLocationApi,
} from '../../api/Api';

export function deleteAllBuildingsAction() {
  return {
    type: DELETE_ALL_BUILDINGS,
  };
}

const startBuildingsLoadingAction = () => ({
  type: START_BUILDINGS_LOAD,
});

// an action that describes which state to update when
// adding buildings
function addBuildingsAction(buildings) {
  return {
    type: ADD_BUILDINGS,
    buildings,
    receivedAt: Date.now(),
  };
}

// eslint-disable-next-line import/no-unused-modules
export function invalidateBuildingsAction() {
  return {
    type: INVALIDATE_BUILDINGS,
  };
}

// an action creator which handles making the GET buildings network request
// and dispatching an action once network response/error returns
export default function fetchBuildingsActionCreator() {
  // Redux Thunk allows this, see its docs for more detail
  return (dispatch, getState) => {
    const state = getState();

    // if current buildings already fetched and  not invalidated, abort fetch
    if (
      (!state.entities.buildings.meta.isInvalid && state.entities.buildings.meta.receivedAt) ||
      state.entities.buildings.meta.isLoading
    ) {
      return Promise.resolve();
    }
    dispatch(startBuildingsLoadingAction());

    return getAll(state.user.jwt, 'buildings').then(
      (response) => {
        dispatch(addBuildingsAction(response));
      },
      // eslint-disable-next-line no-console
      (error) => console.log(error)
    );
  };
}

export const patchBuildingActionCreator = (buildingId, patch) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();

  return (
    patchBuilding(buildingId, patch, jwt)
      // eslint-disable-next-line no-console
      .catch((e) => console.error(`Error patching building ${e}`))
      .finally(() => {
        dispatch(invalidateBuildingsAction());
        dispatch(fetchBuildingsActionCreator());
      })
  );
};

export const createBuildingActionCreator =
  ({ name, orgId, address, purpose, timezone, area, areaUnitId }) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();

    return createBuilding(jwt, name, orgId, address, purpose, timezone, area, areaUnitId).then(
      (building) => {
        dispatch(addBuildingsAction([building]));
        return building;
      }
    );
  };

export const deleteBuildingActionCreator = (buildingId) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();

  return (
    deleteBuilding(buildingId, jwt)
      // eslint-disable-next-line no-console
      .catch((e) => console.error(`Error deleting building ${e}`))
      .finally(() => {
        dispatch(deleteAllBuildingsAction());
        dispatch(fetchBuildingsActionCreator());
      })
  );
};

function addMultipleLocationsAction(buildingId, { locations }) {
  return {
    type: ADD_MULTIPLE_LOCATIONS,
    buildingId,
    locations,
  };
}

function deleteLocationsAction(buildingId, { locations_deleted }) {
  return {
    type: DELETE_MULTIPLE_LOCATIONS,
    buildingId,
    locations_deleted,
  };
}

export const fetchLocationsForBuildingAction =
  (buildingId, force = false) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();

    const state = getState();
    const locations = state.entities.buildings.locationsByBuildingIdName[buildingId];
    // If we have already loaded into redux, don't reload
    if (locations && !force) return Promise.resolve();
    return getAllLocationsForBuilding(buildingId, jwt).then((response) => {
      // dispatch action to add locations
      dispatch(addMultipleLocationsAction(buildingId, response));
    });
  };

export const createLocation = (buildingId, name, type) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return createLocationApi(jwt, buildingId, name, type).then((response) =>
    dispatch(addMultipleLocationsAction(buildingId, { locations: [response] }))
  );
};

export const modifyLocations = (buildingId, nameNewNames) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return modifyLocationsApi(jwt, buildingId, nameNewNames).then((response) =>
    Promise.all([
      dispatch(addMultipleLocationsAction(buildingId, response)),
      dispatch(
        deleteLocationsAction(buildingId, {
          locations_deleted: nameNewNames.map((pair) => pair.name),
        })
      ),
    ])
  );
};

export const deleteLocations = (buildingId, names) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return deleteLocationApi(jwt, buildingId, names).then((response) =>
    dispatch(deleteLocationsAction(buildingId, response))
  );
};

export const createLink =
  (buildingId, linkType, sourceEquipmentId, fedLocationNames, fedEquipmentIds) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();
    return createRelationships(
      jwt,
      buildingId,
      linkType,
      sourceEquipmentId,
      fedLocationNames,
      fedEquipmentIds
    ).then((response) =>
      Promise.all([
        dispatch(addMultipleLocationsAction(buildingId, response)),
        dispatch(addMultipleEquipmentAndPointsAction(response)),
      ])
    );
  };

export const deleteLink =
  (buildingId, linkType, sourceEquipmentId, unfedLocationNames, unfedEquipmentIds) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();
    return deleteRelationships(
      jwt,
      buildingId,
      linkType,
      sourceEquipmentId,
      unfedLocationNames,
      unfedEquipmentIds
    ).then((response) =>
      Promise.all([
        dispatch(addMultipleLocationsAction(buildingId, response)),
        dispatch(addMultipleEquipmentAndPointsAction(response)),
      ])
    );
  };

export const createContains =
  (buildingId, sourceLocationNames, containedLocationNames, containedEquipmentIds) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();
    return createContainsRelationships(
      jwt,
      buildingId,
      sourceLocationNames,
      containedLocationNames,
      containedEquipmentIds
    ).then((response) =>
      Promise.all([
        dispatch(addMultipleLocationsAction(buildingId, response)),
        dispatch(addMultipleEquipmentAndPointsAction(response)),
      ])
    );
  };

export const deleteContains =
  (buildingId, sourceLocationNames, uncontainedLocationNames, uncontainedEquipmentIds) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();
    return deleteContainsRelationships(
      jwt,
      buildingId,
      sourceLocationNames,
      uncontainedLocationNames,
      uncontainedEquipmentIds
    ).then((response) =>
      Promise.all([
        dispatch(addMultipleLocationsAction(buildingId, response)),
        dispatch(addMultipleEquipmentAndPointsAction(response)),
      ])
    );
  };
